import {IonItem, IonLabel} from "@ionic/react";
import {type Ref, useMemo, useRef} from "react";
import {isNil} from "app/utils/stdlib";
import {hofForwardRef, type MaybeNil} from "app/utils/types";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {RequiredLabel} from "app/employee/incdoc/RequiredLabel";
import {ErrorMessage} from "app/employee/incdoc/supplier/ErrorMessage";
import {IdSingleComboBox, type IdSingleComboBoxProps} from "app/employee/controlCard/IdSingleComboBox";

export interface TypeaheadSelectProps<Option, Value> extends IdSingleComboBoxProps<Option, Value> {
  required?: boolean;
  disabled?: boolean;
  errorMessage?: string;
}
export const TypeaheadSelect = hofForwardRef(
  <Option, Value>(props: TypeaheadSelectProps<Option, Value>, ref: Ref<HTMLIonItemElement>) => {
    const {title, required, disabled, errorMessage, onSelect, value, options, isEqual, textExtractor} = props;
    const modal = useRef<HTMLIonModalElement>(null);

    const handleSelect = async (option: MaybeNil<Option>) => {
      await onSelect?.(option);
      await modal.current?.dismiss();
    };

    const selectedValue = useMemo(() => {
      if (!isNil(options) && !isNil(value)) {
        for (const option of options) {
          if (isEqual(option, value)) {
            return textExtractor(option);
          }
        }
      }
      return null;
    }, [isEqual, options, textExtractor, value]);

    const label = (
      <>
        {title} {required && isNil(errorMessage) ? <RequiredLabel value={selectedValue} /> : null}
      </>
    );

    return (
      <>
        <IonItem button detail disabled={disabled} onClick={() => modal.current?.present()} ref={ref}>
          <IonLabel className="ion-text-wrap">
            {isNil(selectedValue) ? (
              label
            ) : (
              <>
                <p>{label}</p> {selectedValue}
              </>
            )}
            {errorMessage ? (
              <p>
                <ErrorMessage>{errorMessage}</ErrorMessage>
              </p>
            ) : null}
          </IonLabel>
        </IonItem>
        <FstIonModal ref={modal}>
          <IdSingleComboBox {...props} onSelect={handleSelect} />
        </FstIonModal>
      </>
    );
  }
);
